import $ from 'jquery';
import {gsap, ScrollTrigger} from './wrapper/gsap.mjs';
import './ui/Modal.mjs';
import {isIE, isTouchDevice} from './utilities/user-agent.mjs';
import OffCanvas from './ui/OffCanvas.mjs';
import Collapse from './ui/Collapse.mjs';
import Footer from './ui/Footer.mjs';
import Form from './ui/Form.mjs';
import Header from './ui/Header.mjs';
import {initLanguageSwitch} from './ui/LanguageSwitch.mjs';
import {initVideoHandling} from './ui/Video.mjs';
import {initTeaserEditorialLineTextExpand} from './../component/teaser-editorial-line/TeaserEditorialLine.mjs';
import {
  devicePrefersReducedMotion,
  gridBreakpoints,
} from './utilities/media-queries.mjs';
import {formatNumber} from './utilities/format.mjs';

export const CSS_CLASS_ANIMATED_LINE = '.ef-animate-line';

class Ui {
  constructor() {
    return new Promise((resolve) => {
      initPolyfills();
      this.$window = $(window);
      this.$body = $('body');
      initHelperClasses.call(this);
      initRevealAnimation();
      initLineAnimation();
      this.Header = new Header(this);
      this.Footer = new Footer(this);
      this.Form = new Form();
      new OffCanvas()
      new Collapse();
      initLanguageSwitch();

      initTeaserEditorialLineTextExpand();
      initVideoHandling();
      initTeaserHeroParallax();
      enableSmoothScrollingForAnchorLinks.call(this);

      resolve(this);
    });
  }
}

function initPolyfills() {
  // Load conditionally, since it's only loaded in head-critical.legacy.js
  if (typeof objectFitImages !== 'undefined') {
    objectFitImages();
  }
}

function initHelperClasses() {
  /**
   * Copy from ui/ui.js
   */
  if (isIE) {
    this.$body.addClass('is-IE');
  }

  this.$body.addClass(() => {
    return isTouchDevice ? 'isTouch' : 'noTouch';
  });

  /*
   * Allow removal of focus style while using mouse
   *
   * @see https://stackoverflow.com/a/51093815
   */
  this.$body.on('mousedown', () => {
    this.$body.addClass('js_using-mouse');
  });
  this.$body.on('keydown', () => {
    this.$body.removeClass('js_using-mouse');
  });
}



/**
 * Each element, which uses the .reveal class, will animate when triggered
 * @param {Object} $elements jQuery nodes to reveal
 */
export function initRevealAnimation($elements = null) {
  $elements = $elements || $('.reveal');
  const elements = $elements.toArray();

  if (elements.length < 1) {
    // Do nothing
    return;
  }

  prepareReveal(elements);

  let start;
  if (devicePrefersReducedMotion.matches === false) {
    // When 50px from the visible element is above the viewport bottom
    start = '-50px bottom';
  }

  ScrollTrigger.batch(elements, {
    // markers: true, // Enable for debug indicators
    interval: .2,
    batchMax: 10,
    once: true,
    onEnter(batch) {
      revealTo(batch);
      countUp(batch);
    },
    start,
  });
}

function countUp(elements) {
  // Start count up for Collection Facts, if available
  elements.forEach((element, index) => {
    if (devicePrefersReducedMotion.matches) {
      // Do nothing
      return;
    }
    if (element.hasAttribute('data-reveal-count') === false) {
      // Do nothing
      return;
    }

    // Find count element
    const count = element.querySelector('[data-count-to]')

    if (!count) {
      return;
    }

    const number = {
      value: parseFloat(count.dataset.countTo),
      decimals: parseInt(count.dataset.countDecimals),
    };
    const decimalSeparator = count.dataset.countDecimalSeparator;
    const thousandsSeparator = count.dataset.countThousandsSeparator;

    gsap.from(number, {
      delay: index * .2, // Sync with stagger
      value: 0,
      snap: {
        value: 0.001
      },
      onUpdate() {
        count.innerText = formatNumber(
          number.value,
          number.decimals,
          decimalSeparator,
          thousandsSeparator,
        );
      },
      duration: 2,
      onStart() {
        element.classList.add('-revealed');
      },
    });
  });
}

/**
 * @param {Array|HTMLElement} elements
 */
export function revealTo(elements) {
  elements.forEach((element, index) => {
    gsap.to(
      element,
      {
        duration: 1.25,
        x: 0,
        y: 0,
        autoAlpha: 1,
        ease: 'expo',
        overwrite: 'auto',
        delay: index * 0.2, // manual stagger effect
        onComplete() {
          element.classList.add('-revealed');
        },
      },
    );
  });
}

/**
 * Read configuration from element and setup for the reveal animation
 * @param {Array|HTMLElement} elements
 */
export function prepareReveal(elements) {
  elements.forEach((element, index) => {
    const shouldRevealToRight = element.classList.contains('-to-right');
    const shouldRevealToLeft = element.classList.contains('-to-left');
    let x = 0;
    let y = 100;

    if (shouldRevealToRight) {
      x = -100;
      y = 0;
    } else if (shouldRevealToLeft) {
      x = 100;
      y = 0;
    }

    // Only fade in if reduced
    if (devicePrefersReducedMotion.matches) {
      x = 0;
      y = 0;
    }

    // Animate
    gsap.set(element, {
      x: x,
      y: y,
      autoAlpha: 0,
    });
  });
}

/**
 * Each element with class .ef-animate-line will add .-animate when triggered
 */
function initLineAnimation() {
  const $elements = $(CSS_CLASS_ANIMATED_LINE);

  $elements.each((index, element) => {
    ScrollTrigger.create({
      // markers: true, // Enable for debug indicators
      trigger: element,
      toggleClass: '-animate',
      // Animate back is disabled for now
      once: true,
    });
  });
}

function enableSmoothScrollingForAnchorLinks() {
  /* Selector explained
   * a[href*=\#] = all links containing "#"
   * :not([href=\#]) = not links containing only a "#"
   * :not([data-toggle]) = not Bootstrap Toggle links (Collapse, Tabs)
   */
  $(document).on('click', 'a[href*=\\#]:not([href=\\#],[data-toggle])', (event) => {
    if (devicePrefersReducedMotion.matches) {
      // Do nothing
      return;
    }

    const target = event.currentTarget;
    const current = window.location;
    const hashValue = event.currentTarget.hash;
    const targetUrl = `${target.protocol}//${target.host + target.pathname + target.search + hashValue}`;
    const currentUrl = `${current.protocol}//${current.host + current.pathname + current.search + hashValue}`;

    // Only accept target URLs if same as current + hash
    if (targetUrl !== currentUrl) {
      return;
    }

    event.preventDefault();

    const $target = $(hashValue);
    if ($target.length) {
      const headerHeight = this.Header.getHeight();
      const targetOffset = $target.offset().top - headerHeight;

      $('html, body').animate({
        scrollTop: targetOffset,
      }, 1000, 'swing');

      // Only update history if URL has changed
      if (targetUrl === current.href) {
        return;
      }

      // Update URL and extend History
      if (typeof window.history.pushState !== 'undefined') {
        window.history.pushState({}, null, targetUrl);
      }
    }
  });
}

function initTeaserHeroParallax() {
  const CSS_CLASS_TEASER = '.ef-teaser-fullscreen';
  const $teaserHero = $(CSS_CLASS_TEASER + '--hero');
  const windowHeight = innerHeight;

  ScrollTrigger.matchMedia({
    // On large devices attach parallax animation
    [`(min-width: ${gridBreakpoints.lg}px) and (prefers-reduced-motion: no-preference)`]: function() {
      $teaserHero.each((index, teaser) => {
        const $teaser = $(teaser);
        const medium = $teaser.find(`${CSS_CLASS_TEASER}--hero__medium-holder`).get(0);
        const image = $teaser.find(`${CSS_CLASS_TEASER}__image`).get(0);
        let isMediumHigherThanWindow = windowHeight < medium.offsetHeight;
        const isSmallTeaser = $teaser.hasClass(`-is-news`);

        if (isSmallTeaser) {
          // Don't animate, since we have not enough image height
          return;
        }

        // Setup
        gsap.set(image, {
          // Consider resize? Good enough for now
          height: isMediumHigherThanWindow ? '100%' : '100vh',
          objectFit: 'cover',
          objectPosition: `50% ${-windowHeight / 2}px`,
        });

        // Animate
        gsap.to(image, {
          objectPosition: `50% ${windowHeight / 2}px`,
          ease: 'none',
          scrollTrigger: {
            // markers: true, // Enable for debug indicators
            trigger: teaser,
            scrub: true,
          },
        });
      });
    },
    // On smaller devices manually cleanup instead of ScrollTrigger.saveStyles()
    // @see https://greensock.com/forums/topic/28364-scrolltriggersavestyles-reset-styles-from-gsapset/
    [`(max-width: ${gridBreakpoints.lg - 1}px), (prefers-reduced-motion)`]: function() {
      $teaserHero.each((index, teaser) => {
        const image = $(teaser).find(`${CSS_CLASS_TEASER}__image`).get(0);

        gsap.set(image, {clearProps: 'all'});
      });
    },
  });
}

/*
 * Allows to reference the original Ui in modules without duplication.
 * !!! Consider the promise when importing the Ui into other modules.
 */
export default new Ui();
